import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import { EmptyState } from "@/components/EmptyState";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus,
  Search,
  Trash2,
  Pencil,
  FileDown,
  Loader2,
  ReceiptText,
  CircleDollarSign,
  CheckCircle2,
  Clock3,
  XCircle,
  Send,
  CreditCard,
  AlertTriangle,
  Copy,
  Ban,
  WalletCards,
  History,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_ITEM = {
  description: "",
  quantity: 1,
  unit_price: 0,
  product_id: null,
};

const EMPTY_FORM = {
  client_id: "",
  source_quote_id: "",
  source_work_order_id: "",
  title: "",
  description: "",
  issue_date: "",
  due_date: "",
  status: "borrador",
  payment_method: "transferencia",
  tax_rate: 21,
  notes: "",
  items: [{ ...EMPTY_ITEM }],
};

const STATUS_OPTIONS = [
  { value: "borrador", label: "Borrador" },
  { value: "emitida", label: "Emitida" },
  { value: "parcial", label: "Parcialmente pagada" },
  { value: "pagada", label: "Pagada" },
  { value: "vencida", label: "Vencida" },
  { value: "anulada", label: "Anulada" },
];

const STATUS_META = {
  borrador: {
    label: "Borrador",
    color: "#86868B",
    bg: "#F5F5F7",
  },
  emitida: {
    label: "Emitida",
    color: "#0066CC",
    bg: "#EAF2FC",
  },
  parcial: {
    label: "Pago parcial",
    color: "#AF52DE",
    bg: "#F7EDFC",
  },
  pagada: {
    label: "Pagada",
    color: "#248A3D",
    bg: "#E9F9EE",
  },
  vencida: {
    label: "Vencida",
    color: "#C93400",
    bg: "#FFF4E5",
  },
  anulada: {
    label: "Anulada",
    color: "#D70015",
    bg: "#FFEBEA",
  },
};

const PAYMENT_METHODS = [
  ["transferencia", "Transferencia"],
  ["domiciliacion", "Domiciliación"],
  ["tarjeta", "Tarjeta"],
  ["efectivo", "Efectivo"],
  ["bizum", "Bizum"],
];

function eur(value) {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value) || 0);
}

function todayString() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(dateString, days) {
  const date = new Date(dateString + "T12:00:00");
  date.setDate(date.getDate() + Number(days || 0));
  return date.toISOString().slice(0, 10);
}

function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <div className="mantix-card flex items-center gap-4 p-5">
      <div
        className="flex h-11 w-11 items-center justify-center rounded-2xl"
        style={{ backgroundColor: color + "15" }}
      >
        <Icon className="h-5 w-5" style={{ color }} />
      </div>

      <div>
        <p className="font-display text-2xl font-bold text-[#1D1D1F]">
          {value}
        </p>
        <p className="text-sm text-[#86868B]">{label}</p>
      </div>
    </div>
  );
}

export default function Invoices() {
  const [searchParams, setSearchParams] = useSearchParams();
  const importedQuoteRef = useRef(null);

  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [inventoryProducts, setInventoryProducts] = useState([]);
  const [inventoryProductId, setInventoryProductId] = useState("");
  const [inventoryQuantity, setInventoryQuantity] = useState(1);

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [changingId, setChangingId] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_date: todayString(),
    method: "transferencia",
    notes: "",
  });

  const load = async () => {
    setLoading(true);

    try {
      const [
        invoicesResponse,
        clientsResponse,
        quotesResponse,
        workOrdersResponse,
        organizationResponse,
        inventoryResponse,
      ] = await Promise.all([
        api.get("/invoices"),
        api.get("/clients"),
        api.get("/quotes"),
        api.get("/work-orders"),
        api.get("/organization"),
        api.get("/inventory/products"),
      ]);

      const loadedInvoices = invoicesResponse.data || [];
      const invoicedQuoteIds = new Set(
        loadedInvoices
          .map((invoice) => invoice.source_quote_id)
          .filter(Boolean)
      );

      setInvoices(loadedInvoices);
      setClients(clientsResponse.data || []);
      setQuotes(
        (quotesResponse.data || []).filter(
          (quote) =>
            quote.status === "aceptado" &&
            !invoicedQuoteIds.has(quote.id)
        )
      );
      setWorkOrders(
        (workOrdersResponse.data || []).filter(
          (order) => order.status === "completada"
        )
      );
      setOrganization(organizationResponse.data || null);
      setInventoryProducts(
        (inventoryResponse.data || []).filter(
          (product) => product.active !== false
        )
      );
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const quoteId = searchParams.get("quote");

    if (
      loading ||
      !quoteId ||
      importedQuoteRef.current === quoteId
    ) {
      return;
    }

    const quote = quotes.find((item) => item.id === quoteId);

    if (!quote) {
      const alreadyInvoiced = invoices.some(
        (invoice) => invoice.source_quote_id === quoteId
      );

      if (alreadyInvoiced) {
        toast.error("Este presupuesto ya tiene una factura creada.");
      } else {
        toast.error("No se encontró el presupuesto aceptado.");
      }

      importedQuoteRef.current = quoteId;
      setSearchParams({}, { replace: true });
      return;
    }

    const issueDate = todayString();
    const dueDays = Number(organization?.payment_due_days ?? 30);

    setEditing(null);
    setForm({
      client_id: quote.client_id || "",
      source_quote_id: quote.id,
      source_work_order_id: "",
      title: quote.title || "",
      description: quote.description || "",
      issue_date: issueDate,
      due_date: addDays(issueDate, dueDays),
      status: "borrador",
      payment_method:
        organization?.default_payment_method || "transferencia",
      tax_rate: quote.tax_rate ?? organization?.default_tax ?? 21,
      notes: quote.notes || "",
      items:
        quote.items?.length > 0
          ? quote.items.map((item) => ({
              description: item.description || "",
              quantity: item.quantity ?? 1,
              unit_price: item.unit_price ?? 0,
              product_id: item.product_id || null,
            }))
          : [{ ...EMPTY_ITEM }],
    });

    importedQuoteRef.current = quoteId;
    setOpen(true);
    setSearchParams({}, { replace: true });
    toast.success("Presupuesto copiado. Revisa la factura antes de guardarla.");
  }, [
    loading,
    quotes,
    invoices,
    organization,
    searchParams,
    setSearchParams,
  ]);

  const resetForm = () => {
    const issueDate = todayString();
    const dueDays = Number(organization?.payment_due_days ?? 30);

    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      issue_date: issueDate,
      due_date: addDays(issueDate, dueDays),
      payment_method:
        organization?.default_payment_method || "transferencia",
      tax_rate: organization?.default_tax ?? 21,
      items: [{ ...EMPTY_ITEM }],
    });
  };

  const openCreate = () => {
    resetForm();
    setOpen(true);
  };

  const openEdit = (invoice) => {
    if (invoice.status === "pagada" || invoice.status === "anulada") {
      toast.error("Las facturas pagadas o anuladas no pueden editarse.");
      return;
    }

    setEditing(invoice);

    setForm({
      client_id: invoice.client_id || "",
      source_quote_id: invoice.source_quote_id || "",
      source_work_order_id: invoice.source_work_order_id || "",
      title: invoice.title || "",
      description: invoice.description || "",
      issue_date: invoice.issue_date
        ? String(invoice.issue_date).slice(0, 10)
        : todayString(),
      due_date: invoice.due_date
        ? String(invoice.due_date).slice(0, 10)
        : "",
      status: invoice.status || "borrador",
      payment_method: invoice.payment_method || "transferencia",
      tax_rate: invoice.tax_rate ?? 21,
      notes: invoice.notes || "",
      items:
        invoice.items?.length > 0
          ? invoice.items.map((item) => ({
              description: item.description || "",
              quantity: item.quantity ?? 1,
              unit_price: item.unit_price ?? 0,
              product_id: item.product_id || null,
            }))
          : [{ ...EMPTY_ITEM }],
    });

    setOpen(true);
  };

  const importQuote = (quoteId) => {
    const quote = quotes.find((item) => item.id === quoteId);

    if (!quote) {
      setForm((current) => ({
        ...current,
        source_quote_id: "",
      }));
      return;
    }

    setForm((current) => ({
      ...current,
      source_quote_id: quote.id,
      source_work_order_id: "",
      client_id: quote.client_id || "",
      title: quote.title || "",
      description: quote.description || "",
      tax_rate: quote.tax_rate ?? current.tax_rate,
      notes: quote.notes || "",
      items:
        quote.items?.length > 0
          ? quote.items.map((item) => ({
              description: item.description || "",
              quantity: item.quantity ?? 1,
              unit_price: item.unit_price ?? 0,
            }))
          : [{ ...EMPTY_ITEM }],
    }));
  };

  const importWorkOrder = (workOrderId) => {
    const order = workOrders.find((item) => item.id === workOrderId);

    if (!order) {
      setForm((current) => ({
        ...current,
        source_work_order_id: "",
      }));
      return;
    }

    const amount = Number(order.amount || 0);
    const taxRate = Number(form.tax_rate || 21);
    const base =
      taxRate > 0
        ? amount / (1 + taxRate / 100)
        : amount;

    setForm((current) => ({
      ...current,
      source_quote_id: "",
      source_work_order_id: order.id,
      client_id: order.client_id || "",
      title: order.title || "",
      description: order.description || "",
      notes: order.materials || "",
      items: [
        {
          description: order.title || "Servicio de mantenimiento",
          quantity: 1,
          unit_price: Number(base.toFixed(2)),
          product_id: null,
        },
      ],
    }));
  };

  const updateItem = (index, key, value) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [key]: value,
            }
          : item
      ),
    }));
  };

  const addItem = () => {
    setForm((current) => ({
      ...current,
      items: [...current.items, { ...EMPTY_ITEM }],
    }));
  };

  const addInventoryProduct = () => {
    const product = inventoryProducts.find(
      (item) => item.id === inventoryProductId
    );

    const quantity = Number(inventoryQuantity) || 0;

    if (!product) {
      toast.error("Selecciona un material del inventario.");
      return;
    }

    if (quantity <= 0) {
      toast.error("Introduce una cantidad válida.");
      return;
    }

    setForm((current) => {
      const firstIsEmpty =
        current.items.length === 1 &&
        !current.items[0].description.trim();

      const newItem = {
        description: `${product.name}${product.sku ? ` (${product.sku})` : ""}`,
        quantity,
        unit_price: Number(product.sale_price || 0),
        product_id: product.id,
      };

      return {
        ...current,
        items: firstIsEmpty
          ? [newItem]
          : [...current.items, newItem],
      };
    });

    setInventoryProductId("");
    setInventoryQuantity(1);
    toast.success("Material añadido a la factura.");
  };

  const removeItem = (index) => {
    setForm((current) => {
      if (current.items.length === 1) {
        return current;
      }

      return {
        ...current,
        items: current.items.filter(
          (_, itemIndex) => itemIndex !== index
        ),
      };
    });
  };

  const totals = useMemo(() => {
    const subtotal = form.items.reduce((sum, item) => {
      const quantity = Number(item.quantity) || 0;
      const unitPrice = Number(item.unit_price) || 0;
      return sum + quantity * unitPrice;
    }, 0);

    const tax = subtotal * ((Number(form.tax_rate) || 0) / 100);

    return {
      subtotal,
      tax,
      total: subtotal + tax,
    };
  }, [form.items, form.tax_rate]);

  const save = async (event) => {
    event.preventDefault();

    if (!form.client_id) {
      toast.error("Selecciona un cliente.");
      return;
    }

    if (!form.title.trim()) {
      toast.error("Escribe el concepto de la factura.");
      return;
    }

    if (!form.issue_date) {
      toast.error("Selecciona la fecha de emisión.");
      return;
    }

    const cleanItems = form.items
      .map((item) => ({
        description: item.description.trim(),
        quantity: Number(item.quantity) || 0,
        unit_price: Number(item.unit_price) || 0,
        product_id: item.product_id || null,
      }))
      .filter(
        (item) =>
          item.description &&
          item.quantity > 0
      );

    if (!cleanItems.length) {
      toast.error("Añade al menos una partida válida.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...form,
        title: form.title.trim(),
        description: form.description.trim(),
        notes: form.notes.trim(),
        due_date: form.due_date || null,
        source_quote_id: form.source_quote_id || null,
        source_work_order_id: form.source_work_order_id || null,
        tax_rate: Number(form.tax_rate) || 0,
        items: cleanItems,
      };

      if (editing) {
        await api.put("/invoices/" + editing.id, payload);
        toast.success("Factura actualizada.");
      } else {
        await api.post("/invoices", payload);
        toast.success("Factura creada.");
      }

      setOpen(false);
      resetForm();
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (invoice, status) => {
    setChangingId(invoice.id);

    try {
      await api.put("/invoices/" + invoice.id, {
        status,
      });

      setInvoices((current) =>
        current.map((item) =>
          item.id === invoice.id
            ? { ...item, status }
            : item
        )
      );

      toast.success("Estado actualizado.");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setChangingId(null);
    }
  };

  const remove = async (invoice) => {
    if (invoice.status === "pagada") {
      toast.error("No puedes eliminar una factura pagada.");
      return;
    }

    const confirmed = window.confirm(
      '¿Seguro que quieres eliminar la factura "' +
        invoice.number +
        '"?'
    );

    if (!confirmed) {
      return;
    }

    setDeletingId(invoice.id);

    try {
      await api.delete("/invoices/" + invoice.id);

      setInvoices((current) =>
        current.filter((item) => item.id !== invoice.id)
      );

      toast.success("Factura eliminada.");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeletingId(null);
    }
  };

  const openPayment = (invoice) => {
    const pending = Number(
      invoice.pending_amount ?? invoice.total ?? 0
    );

    if (pending <= 0) {
      toast.success("Esta factura ya está totalmente pagada.");
      return;
    }

    setPaymentInvoice(invoice);
    setPaymentForm({
      amount: pending.toFixed(2),
      payment_date: todayString(),
      method: invoice.payment_method || "transferencia",
      notes: "",
    });
    setPaymentOpen(true);
  };

  const registerPayment = async (event) => {
    event.preventDefault();

    if (!paymentInvoice || Number(paymentForm.amount) <= 0) {
      toast.error("Introduce un importe válido.");
      return;
    }

    setPaymentSaving(true);
    try {
      await api.post(
        `/invoices/${paymentInvoice.id}/payments`,
        {
          ...paymentForm,
          amount: Number(paymentForm.amount),
        }
      );
      toast.success("Cobro registrado.");
      setPaymentOpen(false);
      setPaymentInvoice(null);
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setPaymentSaving(false);
    }
  };

  const duplicateInvoice = async (invoice) => {
    try {
      await api.post(`/invoices/${invoice.id}/duplicate`);
      toast.success("Factura duplicada como borrador.");
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const voidInvoice = async (invoice) => {
    if (!window.confirm(`¿Anular la factura ${invoice.number}?`)) {
      return;
    }

    try {
      await api.post(`/invoices/${invoice.id}/void`);
      toast.success("Factura anulada.");
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const openPdf = (invoice) => {
    const token = localStorage.getItem("mantix_token");

    window.open(
      api.defaults.baseURL +
        "/invoices/" +
        invoice.id +
        "/pdf?auth=" +
        encodeURIComponent(token || ""),
      "_blank",
      "noopener,noreferrer"
    );
  };

  const filteredInvoices = useMemo(() => {
    const normalized = query.trim().toLowerCase();

    return invoices.filter((invoice) => {
      const matchesQuery =
        !normalized ||
        [
          invoice.number,
          invoice.title,
          invoice.client_name,
          invoice.status,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(normalized);

      const matchesStatus =
        statusFilter === "todos" ||
        invoice.status === statusFilter;

      return matchesQuery && matchesStatus;
    });
  }, [invoices, query, statusFilter]);

  const paidInvoices = invoices.filter(
    (invoice) => invoice.status === "pagada"
  );

  const issuedInvoices = invoices.filter(
    (invoice) => invoice.status === "emitida"
  );

  const overdueInvoices = invoices.filter(
    (invoice) => invoice.status === "vencida"
  );

  const paidTotal = paidInvoices.reduce(
    (sum, invoice) => sum + Number(invoice.total || 0),
    0
  );

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <Dialog
        open={paymentOpen}
        onOpenChange={(value) => {
          setPaymentOpen(value);
          if (!value) setPaymentInvoice(null);
        }}
      >
        <DialogContent className="max-w-lg rounded-3xl border-none bg-white p-6">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Registrar cobro
            </DialogTitle>
            <p className="text-sm text-[#86868B]">
              {paymentInvoice?.number} · Pendiente:{" "}
              {eur(
                paymentInvoice?.pending_amount ??
                  paymentInvoice?.total ??
                  0
              )}
            </p>
          </DialogHeader>

          <form onSubmit={registerPayment} className="mt-5 space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">
                Importe cobrado
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                max={
                  paymentInvoice?.pending_amount ??
                  paymentInvoice?.total ??
                  undefined
                }
                value={paymentForm.amount}
                onChange={(event) =>
                  setPaymentForm({
                    ...paymentForm,
                    amount: event.target.value,
                  })
                }
                required
                className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  Fecha
                </label>
                <input
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(event) =>
                    setPaymentForm({
                      ...paymentForm,
                      payment_date: event.target.value,
                    })
                  }
                  required
                  className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  Método
                </label>
                <select
                  value={paymentForm.method}
                  onChange={(event) =>
                    setPaymentForm({
                      ...paymentForm,
                      method: event.target.value,
                    })
                  }
                  className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                >
                  {PAYMENT_METHODS.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">
                Nota
              </label>
              <textarea
                rows={3}
                value={paymentForm.notes}
                onChange={(event) =>
                  setPaymentForm({
                    ...paymentForm,
                    notes: event.target.value,
                  })
                }
                className="w-full resize-none rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
              />
            </div>

            <button
              type="submit"
              disabled={paymentSaving}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#0066CC] py-3 font-medium text-white disabled:opacity-60"
            >
              {paymentSaving && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              {paymentSaving ? "Guardando..." : "Registrar cobro"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <PageHeader
        title="Facturas"
        subtitle={
          invoices.length +
          " facturas · " +
          eur(
            invoices.reduce(
              (sum, invoice) =>
                sum + Number(invoice.total || 0),
              0
            )
          ) +
          " en total"
        }
        action={
          <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
              setOpen(nextOpen);

              if (!nextOpen) {
                resetForm();
              }
            }}
          >
            <DialogTrigger asChild>
              <button
                data-testid="new-invoice-btn"
                type="button"
                onClick={openCreate}
                className="flex items-center gap-2 rounded-full bg-[#0066CC] px-5 py-2.5 font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.3)] transition-all hover:-translate-y-0.5 hover:bg-[#004499]"
              >
                <Plus className="h-4 w-4" />
                Nueva factura
              </button>
            </DialogTrigger>

            <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto rounded-3xl border-none bg-white p-6 shadow-2xl">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-bold text-[#1D1D1F]">
                  {editing
                    ? "Editar factura"
                    : "Nueva factura"}
                </DialogTitle>

                <p className="mt-1 text-sm text-[#86868B]">
                  Crea una factura manual o impórtala desde un presupuesto u orden completada.
                </p>
              </DialogHeader>

              <form onSubmit={save} className="mt-4 space-y-5">
                {form.source_quote_id && (
                  <div className="rounded-2xl border border-[#B7E4C7] bg-[#E9F9EE] p-4 text-sm text-[#248A3D]">
                    Factura creada desde un presupuesto aceptado. Puedes editar
                    cualquier dato antes de guardarla.
                  </div>
                )}

                {!editing && (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium">
                        Desde presupuesto aceptado
                      </label>

                      <select
                        value={form.source_quote_id}
                        onChange={(event) =>
                          importQuote(event.target.value)
                        }
                        className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                      >
                        <option value="">
                          No importar presupuesto
                        </option>

                        {quotes.map((quote) => (
                          <option
                            key={quote.id}
                            value={quote.id}
                          >
                            {quote.number} · {quote.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium">
                        Desde orden completada
                      </label>

                      <select
                        value={form.source_work_order_id}
                        onChange={(event) =>
                          importWorkOrder(event.target.value)
                        }
                        className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                      >
                        <option value="">
                          No importar orden
                        </option>

                        {workOrders.map((order) => (
                          <option
                            key={order.id}
                            value={order.id}
                          >
                            {order.number} · {order.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Cliente
                    </label>

                    <select
                      value={form.client_id}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          client_id: event.target.value,
                        })
                      }
                      required
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    >
                      <option value="">
                        Selecciona cliente...
                      </option>

                      {clients.map((client) => (
                        <option
                          key={client.id}
                          value={client.id}
                        >
                          {client.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Estado
                    </label>

                    <select
                      value={form.status}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          status: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option
                          key={status.value}
                          value={status.value}
                        >
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium">
                    Concepto
                  </label>

                  <input
                    value={form.title}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        title: event.target.value,
                      })
                    }
                    required
                    placeholder="Ej. Servicios de mantenimiento de agosto"
                    className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium">
                    Descripción
                  </label>

                  <textarea
                    value={form.description}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        description: event.target.value,
                      })
                    }
                    rows={3}
                    placeholder="Descripción general..."
                    className="w-full resize-none rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Fecha emisión
                    </label>

                    <input
                      type="date"
                      value={form.issue_date}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          issue_date: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Vencimiento
                    </label>

                    <input
                      type="date"
                      value={form.due_date}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          due_date: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Forma de pago
                    </label>

                    <select
                      value={form.payment_method}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          payment_method: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    >
                      {PAYMENT_METHODS.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      IVA (%)
                    </label>

                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={form.tax_rate}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          tax_rate: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-[#D2D2D7]/60 bg-white p-4">
                  <div className="mb-3">
                    <h3 className="font-semibold text-[#1D1D1F]">
                      Materiales del inventario
                    </h3>
                    <p className="text-sm text-[#86868B]">
                      Añade productos reales como partidas. Esta acción no descuenta stock para evitar duplicar salidas ya registradas por una OT.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_120px_auto]">
                    <select
                      value={inventoryProductId}
                      onChange={(event) => setInventoryProductId(event.target.value)}
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    >
                      <option value="">Selecciona material...</option>
                      {inventoryProducts.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.sku ? `${product.sku} · ` : ""}
                          {product.name} · Stock {product.stock} {product.unit || "ud"} · Venta {eur(product.sale_price)}
                        </option>
                      ))}
                    </select>

                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={inventoryQuantity}
                      onChange={(event) => setInventoryQuantity(event.target.value)}
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />

                    <button
                      type="button"
                      onClick={addInventoryProduct}
                      className="rounded-xl bg-[#0066CC] px-5 py-3 text-sm font-medium text-white"
                    >
                      Añadir material
                    </button>
                  </div>
                </div>

                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-[#1D1D1F]">
                        Partidas
                      </h3>
                      <p className="text-sm text-[#86868B]">
                        Detalla los servicios y materiales facturados.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={addItem}
                      className="flex items-center gap-2 rounded-full bg-[#0066CC]/10 px-4 py-2 text-sm font-medium text-[#0066CC]"
                    >
                      <Plus className="h-4 w-4" />
                      Añadir partida
                    </button>
                  </div>

                  <div className="space-y-3">
                    {form.items.map((item, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-1 gap-3 rounded-2xl bg-[#F5F5F7] p-4 md:grid-cols-[minmax(0,1fr)_120px_150px_44px]"
                      >
                        <div>
                          <label className="mb-1 block text-xs font-medium text-[#86868B]">
                            Descripción
                          </label>

                          <input
                            value={item.description}
                            onChange={(event) =>
                              updateItem(
                                index,
                                "description",
                                event.target.value
                              )
                            }
                            placeholder="Ej. Mano de obra"
                            className="w-full rounded-xl bg-white px-3 py-2.5 outline-none"
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-medium text-[#86868B]">
                            Cantidad
                          </label>

                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={item.quantity}
                            onChange={(event) =>
                              updateItem(
                                index,
                                "quantity",
                                event.target.value
                              )
                            }
                            className="w-full rounded-xl bg-white px-3 py-2.5 outline-none"
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-medium text-[#86868B]">
                            Precio unidad
                          </label>

                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(event) =>
                              updateItem(
                                index,
                                "unit_price",
                                event.target.value
                              )
                            }
                            className="w-full rounded-xl bg-white px-3 py-2.5 outline-none"
                          />
                        </div>

                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            disabled={form.items.length === 1}
                            className="flex h-10 w-10 items-center justify-center rounded-xl text-[#B0B0B5] hover:bg-[#FFEBEA] hover:text-[#FF3B30] disabled:opacity-30"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium">
                    Notas
                  </label>

                  <textarea
                    value={form.notes}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        notes: event.target.value,
                      })
                    }
                    rows={3}
                    placeholder="Observaciones, condiciones o referencias..."
                    className="w-full resize-none rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                  />
                </div>

                <div className="rounded-2xl border border-[#D2D2D7]/60 p-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#86868B]">
                      Base imponible
                    </span>
                    <span>{eur(totals.subtotal)}</span>
                  </div>

                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-[#86868B]">
                      IVA ({Number(form.tax_rate) || 0}%)
                    </span>
                    <span>{eur(totals.tax)}</span>
                  </div>

                  <div className="mt-3 flex justify-between border-t border-[#E8E8ED] pt-3 font-semibold">
                    <span>Total</span>
                    <span className="text-[#0066CC]">
                      {eur(totals.total)}
                    </span>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="flex-1 rounded-full border border-[#D2D2D7] bg-white py-3 font-medium hover:bg-[#F5F5F7]"
                  >
                    Cancelar
                  </button>

                  <button
                    type="submit"
                    disabled={saving}
                    className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#0066CC] py-3 font-medium text-white hover:bg-[#004499] disabled:opacity-60"
                  >
                    {saving && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}

                    {saving
                      ? "Guardando..."
                      : editing
                        ? "Guardar cambios"
                        : "Crear factura"}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          icon={ReceiptText}
          label="Facturas"
          value={invoices.length}
          color="#0066CC"
        />

        <SummaryCard
          icon={Clock3}
          label="Emitidas"
          value={issuedInvoices.length}
          color="#FF9500"
        />

        <SummaryCard
          icon={CheckCircle2}
          label="Pagadas"
          value={paidInvoices.length}
          color="#34C759"
        />

        <SummaryCard
          icon={CircleDollarSign}
          label="Cobrado"
          value={eur(paidTotal)}
          color="#5856D6"
        />
      </section>

      {overdueInvoices.length > 0 && (
        <div className="mb-6 flex items-center gap-3 rounded-2xl border border-[#FFD6A5] bg-[#FFF4E5] p-4 text-sm text-[#C93400]">
          <AlertTriangle className="h-5 w-5" />
          Hay {overdueInvoices.length} facturas vencidas pendientes de cobro.
        </div>
      )}

      <section className="mantix-card mb-6 p-4">
        <div className="flex flex-col gap-3 lg:flex-row">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#86868B]" />

            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar por número, concepto o cliente..."
              className="w-full rounded-xl bg-[#F5F5F7] py-3 pl-11 pr-4 text-sm outline-none"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(event) =>
              setStatusFilter(event.target.value)
            }
            className="rounded-xl bg-[#F5F5F7] px-4 py-3 text-sm outline-none"
          >
            <option value="todos">Todos los estados</option>

            {STATUS_OPTIONS.map((status) => (
              <option
                key={status.value}
                value={status.value}
              >
                {status.label}
              </option>
            ))}
          </select>
        </div>

        <p className="mt-3 text-xs text-[#86868B]">
          Mostrando {filteredInvoices.length} de {invoices.length} facturas
        </p>
      </section>

      {loading ? (
        <div className="flex min-h-72 items-center justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-[#0066CC]" />
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div className="mantix-card py-8">
          <EmptyState
            title="No hay facturas"
            subtitle="Crea una factura manual o conviértela desde un presupuesto aceptado."
          />

          <div className="-mt-4 flex justify-center pb-6">
            <button
              type="button"
              onClick={openCreate}
              className="flex items-center gap-2 rounded-full bg-[#0066CC] px-5 py-2.5 text-sm font-medium text-white"
            >
              <Plus className="h-4 w-4" />
              Crear factura
            </button>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-3xl border border-[#D2D2D7]/50 bg-white">
          <div className="hidden grid-cols-[150px_minmax(220px,1fr)_200px_150px_170px_190px] gap-4 border-b border-[#E8E8ED] px-6 py-4 text-xs font-semibold uppercase tracking-wide text-[#86868B] lg:grid">
            <span>Número</span>
            <span>Factura</span>
            <span>Cliente</span>
            <span>Total</span>
            <span>Estado</span>
            <span className="text-right">Acciones</span>
          </div>

          {filteredInvoices.map((invoice) => {
            const meta =
              STATUS_META[invoice.status] ||
              STATUS_META.borrador;

            const isDeleting = deletingId === invoice.id;
            const isChanging = changingId === invoice.id;

            return (
              <article
                key={invoice.id}
                className="grid grid-cols-1 gap-4 border-b border-[#E8E8ED] px-6 py-5 last:border-b-0 lg:grid-cols-[150px_minmax(220px,1fr)_200px_150px_170px_190px] lg:items-center"
              >
                <div>
                  <p className="font-mono text-sm font-semibold text-[#1D1D1F]">
                    {invoice.number}
                  </p>
                  <p className="mt-1 text-xs text-[#86868B]">
                    {invoice.issue_date || "—"}
                  </p>
                </div>

                <div>
                  <h3 className="font-medium text-[#1D1D1F]">
                    {invoice.title}
                  </h3>
                  {invoice.due_date && (
                    <p className="mt-1 text-xs text-[#86868B]">
                      Vence: {invoice.due_date}
                    </p>
                  )}
                </div>

                <p className="text-sm text-[#86868B]">
                  {invoice.client_name || "—"}
                </p>

                <div>
                  <p className="font-mono font-semibold text-[#1D1D1F]">
                    {eur(invoice.total)}
                  </p>
                  {Number(invoice.paid_amount || 0) > 0 && (
                    <p className="mt-1 text-xs text-[#248A3D]">
                      Cobrado: {eur(invoice.paid_amount)}
                    </p>
                  )}
                  {Number(
                    invoice.pending_amount ?? invoice.total ?? 0
                  ) > 0 && (
                    <p className="text-xs text-[#C93400]">
                      Pendiente:{" "}
                      {eur(
                        invoice.pending_amount ??
                          invoice.total ??
                          0
                      )}
                    </p>
                  )}
                </div>

                <select
                  value={invoice.status}
                  disabled={isChanging}
                  onChange={(event) =>
                    changeStatus(invoice, event.target.value)
                  }
                  className="rounded-full px-3 py-2 text-sm font-medium outline-none"
                  style={{
                    color: meta.color,
                    backgroundColor: meta.bg,
                  }}
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option
                      key={status.value}
                      value={status.value}
                    >
                      {status.label}
                    </option>
                  ))}
                </select>

                <div className="flex justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => openEdit(invoice)}
                    title="Editar"
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#EAF2FC] hover:text-[#0066CC]"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    onClick={() => openPdf(invoice)}
                    title="Abrir PDF"
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#EAF2FC] hover:text-[#0066CC]"
                  >
                    <FileDown className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      changeStatus(invoice, "emitida")
                    }
                    title="Marcar como emitida"
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#EAF2FC] hover:text-[#0066CC]"
                  >
                    <Send className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    onClick={() => openPayment(invoice)}
                    title="Registrar cobro"
                    disabled={
                      invoice.status === "anulada" ||
                      Number(
                        invoice.pending_amount ??
                          invoice.total ??
                          0
                      ) <= 0
                    }
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#E9F9EE] hover:text-[#248A3D] disabled:opacity-30"
                  >
                    <WalletCards className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    onClick={() => duplicateInvoice(invoice)}
                    title="Duplicar factura"
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#EAF2FC] hover:text-[#0066CC]"
                  >
                    <Copy className="h-4 w-4" />
                  </button>

                  {invoice.status !== "borrador" &&
                    invoice.status !== "anulada" && (
                      <button
                        type="button"
                        onClick={() => voidInvoice(invoice)}
                        title="Anular factura"
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#FFEBEA] hover:text-[#FF3B30]"
                      >
                        <Ban className="h-4 w-4" />
                      </button>
                    )}

                  <button
                    type="button"
                    onClick={() => remove(invoice)}
                    disabled={
                      isDeleting || invoice.status !== "borrador"
                    }
                    title={
                      invoice.status === "borrador"
                        ? "Eliminar"
                        : "Solo se eliminan borradores"
                    }
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-[#B0B0B5] hover:bg-[#FFEBEA] hover:text-[#FF3B30] disabled:opacity-50"
                  >
                    {isDeleting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}